import { Trans, useLingui } from '@lingui/react/macro';
import { useFormikContext } from 'formik';
import type { ChampionshipFormValues } from 'modules/championship/ui/components/form-parts/rules';
import { GameRuleProps } from 'modules/championship/ui/components/form-parts/rules/types';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { UDFormLabeledCheckbox, UDFormLabeledInput } from 'ud-form/components';
import { UDSectionTitle } from 'ud-ui/components';

interface ExtendedGameRuleProps extends GameRuleProps {
  children?: ReactNode;
}

const GameBase: React.FC<ExtendedGameRuleProps> = ({ sportType = 'general', children }) => {
  const { t } = useLingui();
  const context = useFormikContext<ChampionshipFormValues>();
  const { gamePeriodsNumber, periodTime } = useMemo(() => {
    const settings = context.values.settings;
    const gamePeriodsNumber = Number(settings.gamePeriodsNumber) || 0;
    const periodTime = Number(settings.periodTime) || 0;
    return { gamePeriodsNumber, periodTime };
  }, [context.values.settings]);

  useEffect(() => {
    // If either periods number or period time become non-positive, force countdownTimer to false
    if (gamePeriodsNumber <= 0 || periodTime <= 0) {
      if (context.values.settings.countdownTimer) {
        context.setFieldValue('settings.countdownTimer', false);
      }
    }
  }, [gamePeriodsNumber, periodTime, context.values.settings.countdownTimer, context.setFieldValue, context.values.settings]);

  return (
    <>
      <UDSectionTitle title={t`Игра`} icon="⚙️" tooltip={t`Технические параметры матча.`} />

      <div className="grid grid-cols-12 gap-x-12">
        <div className="col-span-6">
          <UDFormLabeledInput
            name="settings.gamePeriodsNumber"
            placeholder="0"
            label={t`Количество периодов в матче`}
            alignRight={true}
            flexShrink={true}
            tooltip={t`Число таймов/периодов в игре (например, 2 в футболе, 3 в хоккее).`}
            tooltipProps={{ position: 'input-right' }}
          />
          <UDFormLabeledInput
            name="settings.periodTime"
            placeholder={t`0 мин`}
            label={t`Длительность основного периода`}
            alignRight={true}
            flexShrink={true}
            tooltip={t`Длительность одного основного периода в минутах (без учёта остановок). Например, 45 для футбола.`}
            tooltipProps={{ position: 'input-right' }}
          />
          <UDFormLabeledInput
            name="settings.totalMatches"
            placeholder={t`Не указано`}
            label={t`Количество матчей`}
            containerClassName="flex items-center"
            alignRight={true}
            flexShrink={true}
            tooltip={
              <div style={{ whiteSpace: 'pre-line' }}>
                {t`Количество игр в турнире.

Эта настройка будет показывать прогресс завершённости турнира.`}
              </div>
            }
            tooltipProps={{ position: 'input-right' }}
          />
        </div>
        <div className="col-span-6">
          <UDFormLabeledInput
            name="settings.gameExtraPeriodsNumber"
            placeholder="0"
            label={t`Кол-во периодов дополнительного времени`}
            alignRight={true}
            flexShrink={true}
            tooltip={t`Количество периодов дополнительного времени (например, 2 тайма в футболе).`}
            tooltipProps={{ position: 'input-right' }}
          />

          <UDFormLabeledInput
            name="settings.periodExtraTime"
            placeholder={t`0 мин`}
            label={t`Длительность дополнительного периода`}
            alignRight={true}
            flexShrink={true}
            tooltip={t`Продолжительность одного дополнительного периода в минутах. (например, 15 минут в футболе).`}
            tooltipProps={{ position: 'input-right' }}
          />

          <UDFormLabeledInput
            name="settings.penaltyShootoutCount"
            placeholder="0"
            label={t`Количество ударов в серии пенальти`}
            alignRight={true}
            flexShrink={true}
            tooltip={t`Количество ударов в серии послематчевых пенальти (например, 5 в футболе).`}
            tooltipProps={{ position: 'input-right' }}
          />

          <UDFormLabeledCheckbox
            label={t`Обратный отсчёт времени`}
            name="settings.countdownTimer"
            disabled={gamePeriodsNumber <= 0 || periodTime <= 0}
            tooltip={
              <div style={{ whiteSpace: 'pre-line' }}>
                <span>
                  <Trans>
                    В режиме трансляции таймер будет идти в обратно направлении. Напрямую зависит от
                    <strong> длительности мачта</strong> и <strong>кол-во периодов в матче</strong>, они должны быть больше 0.
                  </Trans>
                </span>
                <br />
                <br />
                <span>
                  <Trans>
                    ⚠️ Время события матча будет записываться в зависимости от прошедшего времени, как если таймер отсчитывался с
                    00:00.
                  </Trans>
                </span>
              </div>
            }
            tooltipProps={{ position: 'input-right' }}
          />
        </div>
      </div>

      {/* Место для дополнительных полей */}
      {children}
    </>
  );
};

export default GameBase;
